import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

import FormInput from 'components/forms/FormInput';
import FormTextarea from 'components/forms/FormTextarea';
import RichEditorContent from 'components/RichEditorContent';
import LegalTextHelper from 'components/layout/LegalTextHelper';
import ModalPrivacyPolicy from 'components/modals/ModalPrivacyPolicy';

const CmsElementContact = (props) => {
  const {
    editMode,
    index,
    showElementSettings,
    intl
  } = props;

  // Local State for Privacy Modal
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const messages = intl?.messages || {};

  // CMS Logic Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: index,
    disabled: !editMode
  });

  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    cursor: editMode ? 'move' : 'default',
    width: '100%'
  };

  // Handlers
  const handleShowPrivacyPolicy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrivacyPolicy(true);
  };

  const getPrivacyPolicyText = () => {
    const text = messages.contact_agreement_text;
    const substitutes = {
      '{data_use}': (
        <a href="#dataUse" onClick={handleShowPrivacyPolicy}>
          {messages.signup_agreement_text_data_use}
        </a>
      ),
    };
    return <LegalTextHelper text={text} substitutes={substitutes} />;
  };

  // Visual Logic
  const isVisible = editMode || shown;
  const shownClass = isVisible ? 'elem-shown' : 'elem-hidden';
  const textClass = `text-${getParamValue('mainColor', 'master')}`;
  const textAlign = getParamValue('textAlign', 'center');

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-contact ${shownClass} ${cssClasses} ${responsiveClasses}`.trim()}
      style={getBackgroundStyle()}
      onClick={(e) => editMode && showElementSettings(e, index)}
    >
      {elementStyleTag}

      <div className={`ms-contact-container text-${textAlign} ${textClass}`}>
        <h2 className="no-margin p-b-25">{getParamValue('title')}</h2>
        <RichEditorContent content={`<p>${getParamValue('content', '')}</p>`} />
      </div>

      <div className="m-t-15 m-b-15 form-group-attached">
        {/* In the CMS preview, these fields are usually static/empty */}
        <FormInput
          value=""
          onChange={() => {}}
          label={messages.email}
          type="email"
        />
        <FormInput
          value=""
          onChange={() => {}}
          label={messages.client_name}
        />
        <FormTextarea
          value=""
          onChange={() => {}}
          label={messages.message}
        />
      </div>

      <p className={textClass}>{getPrivacyPolicyText()}</p>

      <div className="text-right">
        <Button
          className="call-to-action btn-animated from-left fa fa-arrow-right"
          variant="primary"
          onClick={(e) => e.stopPropagation()} // Prevent settings opening on button click
        >
          <span>{messages.send}</span>
        </Button>
      </div>

      {getAppendHtml()}

      <ModalPrivacyPolicy
        show={showPrivacyPolicy}
        close={() => setShowPrivacyPolicy(false)}
      />
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
    >
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
};

export default CmsElementContact;
